<template>
  <el-dialog
    :title="title"
    :visible.sync="visible"
    append-to-body
    width="100%"
    class="rate-startup-dialog"
    :close-on-click-modal="false"
  >
    <div class="categories">
      <RateCategory
        v-for="(rating, category) in ratingCategories"
        ref="ratingCategories"
        :key="category"
        v-model="ratingCategories[category]"
        :category="category"
        :read-only="readOnly"
        @input="onRate"
      />
    </div>

    <pre hidden>{{ ratingCategories | json }}</pre>

    <template slot="footer">
      <el-button @click="close"> Cancel </el-button>
      <el-button :loading="processing" type="primary" @click="submitRating">
        Submit
      </el-button>
    </template>
  </el-dialog>
</template>

<script>
import RateCategory from './RateCategory.vue'
import _ from 'lodash'
import { mapState } from 'vuex'
import { RATING_CATEGORIES } from 'services/rating-categories'

export default {
  components: { RateCategory },
  props: {
    startupId: { type: String, default: () => '' },
    title: { type: String, default: 'Rate company' },
    readOnly: { type: Boolean, default: false },
  },
  computed: {
    ...mapState({
      errors: (s) => s.startupOwners.errors,
      processing: (s) => s.startupProfile.rateInProgress,
      startup: (s) => s.startupProfile.startup,
    }),
    showroomId() {
      const s = this.$store.state
      if (s.eventDetails.event && s.eventDetails.event.id) {
        return s.eventDetails.event.id
      } else if (this.$route.query.showroomId) {
        return this.$route.query.showroomId
      }
      return null
    },
  },
  data() {
    return {
      visible: false,
      ratingCategories: this.getEmptyRatingCategories(),
    }
  },
  methods: {
    getEmptyRatingCategories() {
      return _.fromPairs(
        RATING_CATEGORIES.map((category) => {
          return [category, { value: 0, note: '' }]
        }),
      )
    },

    open(ratings = {}) {
      this.ratingCategories = this.getEmptyRatingCategories()
      _.forOwn(ratings, (categoryRating, category) => {
        if (!this.ratingCategories[category]) {
          this.ratingCategories[category] = {}
        }
        this.ratingCategories[category].value = categoryRating.value
        this.ratingCategories[category].note = categoryRating.note
      })

      this.$nextTick(() => {
        this.hideNotes()
      })

      this.visible = true
      if (this.startup && this.startup.id) {
        this.setDataFromStartup()
      }
    },

    setDataFromStartup() {
      RATING_CATEGORIES.forEach((category) => {
        this.ratingCategories[category] = {
          value: _.get(this.startup, `ratings.${category}.value`) || 0,
          note: _.get(this.startup, `ratings.${category}.note`) || '',
        }
      })
    },

    hideNotes() {
      this.$refs.ratingCategories.forEach((vm) => {
        if (vm.noteVisible) {
          vm.toggleNoteVisibility()
        }
      })
    },

    reset() {
      this.setDataFromStartup()
    },

    close() {
      this.visible = false
    },

    async submitRating({ close = true } = {}) {
      // NOTE: we are removing reactivtiy at this point as bubling this
      // up would make it a nightmare to track in case any code after this
      // tries to change "ratings". If we would leave reactivity (which is
      // directly connected with vuex) and try to change those values,
      // vuex will be angry on us
      const ratings = JSON.parse(JSON.stringify(this.ratingCategories))
      const data = {
        startupId: this.startupId,
        ratings,
        showroomId: this.showroomId,
      }
      try {
        await this.$store.dispatch('startupProfile/rate', data)
      } catch {
        this.$notify({
          type: 'error',
          title: 'Something went wrong, rating not saved!',
        })
        return
      }
      this.$notify.success('Rating saved')
      this.$emit('rated', data)
      if (close) {
        this.close()
      }
    },
    submitRatingDebounced: _.debounce(function (params) {
      this.submitRating(params)
    }, 500),

    onRate(val) {
      this.submitRatingDebounced({ close: false })
    },
  },
}
</script>

<style lang="scss">
.rate-startup-dialog {
  .el-dialog {
    max-width: 800px;
  }
}
</style>
